/**
 * Always-isolated URL-to-file download.
 *
 * Network body handling and filesystem streaming happen in a supervised,
 * one-use Bun child so a runtime/SDK stall cannot monopolize the host event
 * loop. A fresh child also bounds state retained after each untrusted, potentially
 * large response and lets shutdown reclaim the exact process that handled it;
 * that isolation is intentionally preferred over amortizing child spawn cost.
 * There is deliberately no host-process fallback.
 *
 * @module cpu-offload/download-url-to-path
 */

import { rm } from 'node:fs/promises';
import { performance } from 'node:perf_hooks';
import { withExpensiveSlot } from '../expensive-gate.js';
import { type BrowserLogger, consoleLogger } from '../logger.js';
import type {
  DownloadUrlToPathPayload,
  DownloadUrlToPathResult,
} from './download-url-to-path-task.js';
import { CpuOffloadPool, type CpuOffloadPoolOptions } from './pool.js';

export const DEFAULT_URL_DOWNLOAD_TIMEOUT_MS = 5 * 60_000;
export const DEFAULT_URL_DOWNLOAD_RSS_BYTES = 4 * 1024 * 1024 * 1024;

type UrlDownloadIsolationOptions = {
  timeoutMs?: number;
  rssBytes?: number;
  rssCheckIntervalMs?: number;
  workerPath?: string;
  readRssBytes?: CpuOffloadPoolOptions['readRssBytes'];
  logger?: BrowserLogger;
  signal?: AbortSignal;
  /** @internal Test-only task override for deterministic timeout fixtures. */
  task?: string;
};

export type IsolatedUrlDownload = {
  bytesWritten: number;
  durationMs: number;
  childDurationMs: number;
  childRssHighWaterBytes: number;
};

const removePartialDownload = async (
  destinationPath: string,
  logger: BrowserLogger
): Promise<void> => {
  try {
    await rm(destinationPath, { force: true });
  } catch (error) {
    logger.warn('[UrlDownload] Failed to remove partial download', {
      destinationPath,
      error: error instanceof Error ? error.message : String(error),
    });
  }
};

const runAbortably = async <A>(operation: Promise<A>, signal?: AbortSignal): Promise<A> => {
  if (!signal) return operation;
  if (signal.aborted) throw new Error('isolated download aborted');

  let removeAbortListener = () => {};
  const aborted = new Promise<never>((_resolve, reject) => {
    const onAbort = () => reject(new Error('isolated download aborted'));
    signal.addEventListener('abort', onAbort, { once: true });
    removeAbortListener = () => signal.removeEventListener('abort', onAbort);
  });

  try {
    return await Promise.race([operation, aborted]);
  } finally {
    removeAbortListener();
  }
};

/**
 * Download a URL in a supervised child process.
 *
 * Timeout, RSS, crash, and abort failures reject after reclaiming the child and
 * removing the destination. The signed URL is passed only through framed stdin.
 */
export async function downloadUrlToPathIsolated(
  url: string,
  destinationPath: string,
  options: UrlDownloadIsolationOptions = {}
): Promise<IsolatedUrlDownload> {
  const logger = options.logger ?? consoleLogger;
  const timeoutMs = options.timeoutMs ?? DEFAULT_URL_DOWNLOAD_TIMEOUT_MS;
  const startedAt = performance.now();
  const pool = new CpuOffloadPool({
    size: 1,
    defaultTimeoutMs: timeoutMs,
    rssBytes: options.rssBytes ?? DEFAULT_URL_DOWNLOAD_RSS_BYTES,
    ...(options.rssCheckIntervalMs ? { rssCheckIntervalMs: options.rssCheckIntervalMs } : {}),
    ...(options.workerPath ? { workerPath: options.workerPath } : {}),
    ...(options.readRssBytes ? { readRssBytes: options.readRssBytes } : {}),
    logger,
  });

  let completed = false;
  let result: DownloadUrlToPathResult | undefined;
  let childRssHighWaterBytes = 0;
  try {
    const operation = withExpensiveSlot(() =>
      pool.run<DownloadUrlToPathResult>(
        options.task ?? 'download-url-to-path',
        { url, destinationPath } satisfies DownloadUrlToPathPayload,
        { timeoutMs }
      )
    );
    result = await runAbortably(operation, options.signal);
    childRssHighWaterBytes = pool.stats().rssHighWaterBytes;
    completed = true;
  } finally {
    await pool.shutdown();
    if (!completed) await removePartialDownload(destinationPath, logger);
  }

  if (!result) throw new Error('isolated download completed without a result');
  return {
    bytesWritten: result.bytesWritten,
    durationMs: performance.now() - startedAt,
    childDurationMs: result.childDurationMs,
    childRssHighWaterBytes,
  };
}
